import { useEffect, useRef, useState, useCallback } from "react";
import { useScribe } from "@elevenlabs/react";
import { io } from "socket.io-client";
import {
  Sun,
  Moon,
  Plus,
  LogOut,
  Download,
  X,
  Send,
  MessageSquare,
  Check,
  CheckCheck,
  Mic,
  MicOff,
} from "lucide-react";
import toast from "react-hot-toast";
import api from "../api/index";
import { useTheme } from "../context/ThemeContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function ChatApp({ currentUser, setCurrentUser }) {
  const { theme: t, isDark, toggleTheme } = useTheme();

  const socketRef = useRef(null);
  const [socketReady, setSocketReady] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const [interrupt, setInterrupt] = useState(null);
  const [peerInterim, setPeerInterim] = useState(null);
  const [isDictating, setIsDictating] = useState(false);

  const finalTextAccumRef = useRef("");
  const newMessageRef = useRef("");
  const selectedConvRef = useRef(selectedConv);
  const messagesEndRef = useRef(null);
  const typingStartedRef = useRef(false);
  const typingTimeoutRef = useRef(null);

  // Segment tracking
  const dictateStartTimeRef = useRef(null);
  const segmentsRef = useRef([]); // [{start_ms, end_ms, text}]
  const lastCommitTimeRef = useRef(null);

  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  const setMsg = useCallback((val) => {
    newMessageRef.current =
      typeof val === "function" ? val(newMessageRef.current) : val;
    setNewMessage(val);
  }, []);

  // ── Socket init ───────────────────────────────────────────────────────────
  useEffect(() => {
    const sio = io(API_URL, {
      query: { user_id: currentUser.id },
      withCredentials: true,
      transports: ["websocket"],
    });
    socketRef.current = sio;
    setSocketReady(true);
    return () => {
      sio.disconnect();
      socketRef.current = null;
      setSocketReady(false);
    };
  }, [currentUser.id]);

  // ── useScribe SDK ─────────────────────────────────────────────────────────
  const scribe = useScribe({
    modelId: "scribe_v2_realtime",
    onPartialTranscript: (data) => {
      const conv = selectedConvRef.current;
      const sio = socketRef.current;
      if (!conv || !sio) return;

      // Partial timestamp tracking
      if (dictateStartTimeRef.current) {
        const elapsed = Date.now() - dictateStartTimeRef.current;
        segmentsRef.current.push({ t_ms: elapsed, text: data.text });
      }

      const display = (finalTextAccumRef.current + " " + data.text).trim();
      newMessageRef.current = display;
      setNewMessage(display);
      sio.emit("dictate_interim", {
        user_id: currentUser.id,
        receiver_id: conv.user.id,
        conversation_id: conv.conversation_id,
        text: display,
      });
    },
    onCommittedTranscript: (data) => {
      const conv = selectedConvRef.current;
      const sio = socketRef.current;
      if (!conv || !sio) return;

      // Segment tracking
      const now = Date.now();
      const startMs = lastCommitTimeRef.current
        ? lastCommitTimeRef.current
        : dictateStartTimeRef.current || now;
      const endMs = now;
      const elapsedStart = startMs - (dictateStartTimeRef.current || now);
      const elapsedEnd = endMs - (dictateStartTimeRef.current || now);
      segmentsRef.current.push({
        start_ms: Math.max(0, elapsedStart),
        end_ms: Math.max(0, elapsedEnd),
        text: data.text,
      });
      lastCommitTimeRef.current = now;

      finalTextAccumRef.current = (
        finalTextAccumRef.current +
        " " +
        data.text
      ).trim();
      newMessageRef.current = finalTextAccumRef.current;
      setNewMessage(finalTextAccumRef.current);
      sio.emit("dictate_interim", {
        user_id: currentUser.id,
        receiver_id: conv.user.id,
        conversation_id: conv.conversation_id,
        text: finalTextAccumRef.current,
      });
    },
  });

  // ── Socket listeners ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!socketReady) return;
    const sio = socketRef.current;
    if (!sio) return;

    sio.on("receive_message", (data) => {
      if (
        String(data.conversation_id) === String(selectedConv?.conversation_id)
      ) {
        setMessages((prev) => [
          ...prev,
          {
            id: data.message_id,
            sender_id: data.sender_id,
            content: data.content,
            timestamp: data.timestamp,
            from_me: String(data.sender_id) === String(currentUser.id),
            read_by:
              String(data.sender_id) !== String(currentUser.id)
                ? [data.sender_id, currentUser.id]
                : [data.sender_id],
          },
        ]);
        setPeerTyping(false);
        setPeerInterim(null);
        if (String(data.sender_id) !== String(currentUser.id)) {
          api.post("/conversations/mark_as_read", {
            message_ids: [data.message_id],
          });
          sio.emit("typing_stop", {
            user_id: data.sender_id,
            receiver_id: currentUser.id,
            conversation_id: data.conversation_id,
          });
        }
      }
      setConversations((prev) =>
        prev.map((c) =>
          String(c.conversation_id) === String(data.conversation_id)
            ? {
                ...c,
                last_message: {
                  from_me: String(data.sender_id) === String(currentUser.id),
                  content: data.content,
                  timestamp: data.timestamp,
                },
                unread_count:
                  String(data.sender_id) !== String(currentUser.id) &&
                  String(data.conversation_id) !==
                    String(selectedConv?.conversation_id)
                    ? (c.unread_count || 0) + 1
                    : 0,
              }
            : c
        )
      );
    });

    sio.on("message_read_update", (data) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === data.message_id ? { ...m, read_by: data.read_by } : m
        )
      );
    });

    sio.on("typing_start", (data) => {
      if (
        String(data.conversation_id) ===
          String(selectedConv?.conversation_id) &&
        String(data.user_id) !== String(currentUser.id)
      )
        setPeerTyping(true);
    });

    sio.on("typing_stop", (data) => {
      if (
        String(data.conversation_id) ===
          String(selectedConv?.conversation_id) &&
        String(data.user_id) !== String(currentUser.id)
      )
        setPeerTyping(false);
    });

    sio.on("dictate_start", (data) => {
      if (
        String(data.conversation_id) ===
          String(selectedConv?.conversation_id) &&
        String(data.user_id) !== String(currentUser.id)
      ) {
        setPeerTyping(false);
        setPeerInterim({ user_id: data.user_id, text: "" });
      }
    });

    sio.on("dictate_interim", (data) => {
      if (
        String(data.conversation_id) ===
          String(selectedConv?.conversation_id) &&
        String(data.user_id) !== String(currentUser.id)
      ) {
        setPeerInterim({ user_id: data.user_id, text: data.text });
        setPeerTyping(false);
      }
    });

    sio.on("dictate_stop", (data) => {
      if (
        String(data.conversation_id) ===
          String(selectedConv?.conversation_id) &&
        String(data.user_id) !== String(currentUser.id)
      ) {
        setPeerInterim(null);
      }
    });

    sio.on("interrupt", (data) => {
      if (
        String(data.conversation_id) === String(selectedConv?.conversation_id)
      ) {
        const isMe = String(data.interrupter_id) === String(currentUser.id);
        setInterrupt({
          name: isMe ? "Sen" : selectedConv?.user?.first_name || "Karşı taraf",
        });
        setTimeout(() => setInterrupt(null), 3000);
      }
    });

    sio.on("user_status", (data) => {
      setConversations((prev) =>
        prev.map((c) =>
          String(c.user?.id) === String(data.user_id)
            ? { ...c, user: { ...c.user, status: data.status } }
            : c
        )
      );
      if (
        selectedConv &&
        String(selectedConv.user?.id) === String(data.user_id)
      ) {
        setSelectedConv((prev) =>
          prev ? { ...prev, user: { ...prev.user, status: data.status } } : prev
        );
      }
    });

    return () => {
      sio.off("receive_message");
      sio.off("message_read_update");
      sio.off("typing_start");
      sio.off("typing_stop");
      sio.off("dictate_start");
      sio.off("dictate_interim");
      sio.off("dictate_stop");
      sio.off("interrupt");
      sio.off("user_status");
    };
  }, [socketReady, selectedConv, currentUser.id]);

  useEffect(() => {
    api.get("/conversations/my").then((r) => setConversations(r.data));
    api.get("/users/available").then((r) => setAvailableUsers(r.data));
  }, []);

  useEffect(() => {
    if (!selectedConv) return;
    setMessagesLoading(true);
    setPeerTyping(false);
    setPeerInterim(null);
    api
      .get(`/conversations/${selectedConv.conversation_id}/messages`)
      .then((r) => {
        const msgs = r.data.map((m) => ({
          ...m,
          from_me: String(m.sender_id) === String(currentUser.id),
        }));
        setMessages(msgs);
        const unread = msgs
          .filter((m) => !m.from_me && !m.read_by?.includes(currentUser.id))
          .map((m) => m.id);
        if (unread.length > 0)
          api.post("/conversations/mark_as_read", { message_ids: unread });
        setConversations((prev) =>
          prev.map((c) =>
            String(c.conversation_id) === String(selectedConv.conversation_id)
              ? { ...c, unread_count: 0 }
              : c
          )
        );
      })
      .finally(() => setMessagesLoading(false));
  }, [selectedConv, currentUser.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, peerTyping, peerInterim]);

  // ── Typing ────────────────────────────────────────────────────────────────
  const emitTypingStop = useCallback(() => {
    const sio = socketRef.current;
    if (typingStartedRef.current && selectedConvRef.current && sio) {
      typingStartedRef.current = false;
      clearTimeout(typingTimeoutRef.current);
      sio.emit("typing_stop", {
        user_id: currentUser.id,
        receiver_id: selectedConvRef.current.user.id,
        conversation_id: selectedConvRef.current.conversation_id,
      });
    }
  }, [currentUser.id]);

  const handleInputChange = (e) => {
    const sio = socketRef.current;
    setMsg(e.target.value);
    if (!sio || !selectedConv) return;
    if (!typingStartedRef.current) {
      typingStartedRef.current = true;
      sio.emit("typing_start", {
        user_id: currentUser.id,
        receiver_id: selectedConv.user.id,
        conversation_id: selectedConv.conversation_id,
      });
    }
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(emitTypingStop, 750);
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = newMessageRef.current.trim();
    if (!text || !selectedConv) return;

    if (isDictating) {
      const conv = selectedConvRef.current;
      const sio = socketRef.current;
      const segmentData =
        segmentsRef.current.length > 0
          ? JSON.stringify(segmentsRef.current)
          : null;

      if (conv && sio) {
        sio.emit("dictate_stop", {
          user_id: currentUser.id,
          receiver_id: conv.user.id,
          conversation_id: conv.conversation_id,
          final_text: text,
          segment_data: segmentData,
        });
      }
      finalTextAccumRef.current = "";
      newMessageRef.current = "";
      segmentsRef.current = [];
      dictateStartTimeRef.current = null;
      lastCommitTimeRef.current = null;
      setMsg("");
      setIsDictating(false);
      setTimeout(() => scribe.disconnect(), 150);
    } else {
      emitTypingStop();
      setMsg("");
      await api.post(
        `/conversations/${selectedConv.conversation_id}/messages`,
        { content: text }
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  // ── Dictate ───────────────────────────────────────────────────────────────
  const stopDictate = useCallback(() => {
    finalTextAccumRef.current = "";
    newMessageRef.current = "";
    segmentsRef.current = [];
    dictateStartTimeRef.current = null;
    lastCommitTimeRef.current = null;
    setNewMessage("");
    setIsDictating(false);
    setTimeout(() => scribe.disconnect(), 150);
  }, [scribe]);

  const startDictate = useCallback(async () => {
    const conv = selectedConvRef.current;
    const sio = socketRef.current;
    if (!conv || !sio) return;

    try {
      const tokenRes = await api.get("/scribe-token");
      const token = tokenRes.data?.token;
      if (!token) {
        toast.error("ElevenLabs token alınamadı.");
        return;
      }

      finalTextAccumRef.current = "";
      newMessageRef.current = "";
      segmentsRef.current = [];
      dictateStartTimeRef.current = Date.now();
      lastCommitTimeRef.current = null;

      await scribe.connect({
        token,
        microphone: { echoCancellation: true, noiseSuppression: true },
      });

      setIsDictating(true);
      sio.emit("dictate_start", {
        user_id: currentUser.id,
        receiver_id: conv.user.id,
        conversation_id: conv.conversation_id,
      });
    } catch (err) {
      if (err.name === "NotAllowedError")
        toast.error("Mikrofon erişimi reddedildi.");
      else toast.error("Dikte başlatılamadı.");
    }
  }, [currentUser.id, scribe]);

  const handleMicToggle = () => {
    if (!isDictating) {
      startDictate();
    } else if (newMessageRef.current.trim()) {
      handleSend();
    } else {
      stopDictate();
    }
  };

  useEffect(() => {
    if (isDictating) stopDictate();
    setMsg("");
    finalTextAccumRef.current = "";
    newMessageRef.current = "";
    segmentsRef.current = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConv?.conversation_id]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const startConversation = async (user) => {
    setShowUserList(false);
    const existing = conversations.find(
      (c) => String(c.user?.id) === String(user.id)
    );
    if (existing) {
      setSelectedConv(existing);
      return;
    }
    const res = await api.post("/conversations/start", {
      receiver_id: user.id,
    });
    const newConv = {
      conversation_id: res.data.conversation_id,
      user,
      last_message: null,
      unread_count: 0,
    };
    setConversations((prev) => [...prev, newConv]);
    setSelectedConv(newConv);
  };

  const deleteConversation = async (convId) => {
    await api.delete(`/conversations/${convId}`);
    setConversations((prev) =>
      prev.filter((c) => c.conversation_id !== convId)
    );
    if (String(selectedConv?.conversation_id) === String(convId))
      setSelectedConv(null);
    toast("Sohbet silindi.");
  };

  const handleExport = async () => {
    if (!selectedConv) return;
    const res = await api.get(
      `/conversations/${selectedConv.conversation_id}/export`
    );
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `turnchat_${selectedConv.conversation_id}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Log dışa aktarıldı.");
  };

  const handleLogout = async () => {
    if (isDictating) stopDictate();
    await api.post("/auth/logout");
    setCurrentUser(null);
  };

  const fullName = (u) =>
    u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username : "";
  const initials = (u) =>
    (u?.first_name?.[0] || u?.username?.[0] || "?").toUpperCase();
  const formatTime = (ts) =>
    ts
      ? new Date(ts).toLocaleTimeString("tr-TR", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  const AvatarEl = ({ user, size = 34 }) => (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: t.accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: size * 0.38,
        flexShrink: 0,
        color: t.accentText,
      }}
    >
      {initials(user)}
    </div>
  );

  const ReadIcon = ({ msg }) => {
    if (!msg.from_me) return null;
    const isRead =
      msg.read_by &&
      msg.read_by.some((id) => String(id) !== String(currentUser.id));
    const peerOnline = selectedConv?.user?.status === "online";
    if (isRead)
      return <CheckCheck size={11} color="#7eb8f7" style={{ opacity: 0.9 }} />;
    if (peerOnline)
      return (
        <CheckCheck size={11} color={t.accentText} style={{ opacity: 0.5 }} />
      );
    return <Check size={11} color={t.accentText} style={{ opacity: 0.5 }} />;
  };

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        height: "100%",
        background: t.surface,
        borderRadius: 14,
        border: `1px solid ${t.border}`,
        overflow: "hidden",
        fontFamily: "'Inter',sans-serif",
        color: t.text,
        transition: "background 0.3s, color 0.3s",
      }}
    >
      {/* SOL PANEL */}
      <div
        style={{
          width: 252,
          borderRight: `1px solid ${t.border}`,
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          background: t.surfaceAlt,
        }}
      >
        <div
          style={{
            padding: "16px 14px 14px",
            borderBottom: `1px solid ${t.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "'Playfair Display',serif",
                fontSize: 17,
                fontWeight: 600,
                color: t.text,
                letterSpacing: 0.3,
              }}
            >
              TurnChat
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
              {fullName(currentUser)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 5 }}>
            {[
              [
                toggleTheme,
                isDark ? <Sun size={13} /> : <Moon size={13} />,
                "Tema",
              ],
              [
                () => {
                  api
                    .get("/users/available")
                    .then((r) => setAvailableUsers(r.data));
                  setShowUserList((v) => !v);
                },
                <Plus size={13} />,
                "Yeni sohbet",
              ],
              [handleLogout, <LogOut size={13} />, "Çıkış"],
            ].map(([fn, icon, title], i) => (
              <button
                key={i}
                onClick={fn}
                title={title}
                style={{
                  background: "transparent",
                  border: `1px solid ${t.border}`,
                  borderRadius: 8,
                  color: t.textMuted,
                  cursor: "pointer",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>

        {showUserList && (
          <div
            style={{
              background: t.surface,
              borderBottom: `1px solid ${t.border}`,
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {availableUsers.length === 0 ? (
              <div
                style={{
                  padding: "14px 16px",
                  color: t.textMuted,
                  fontSize: 12,
                  textAlign: "center",
                }}
              >
                Başka kullanıcı yok.
              </div>
            ) : (
              availableUsers.map((u) => (
                <div
                  key={u.id}
                  onClick={() => startConversation(u)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 14px",
                    cursor: "pointer",
                    borderBottom: `1px solid ${t.border}`,
                  }}
                >
                  <AvatarEl user={u} size={30} />
                  <div>
                    <div
                      style={{ fontSize: 12, fontWeight: 500, color: t.text }}
                    >
                      {fullName(u)}
                    </div>
                    <div
                      style={{ fontSize: 10, color: t.textMuted, marginTop: 1 }}
                    >
                      {u.status}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div style={{ flex: 1, overflowY: "auto", padding: "6px" }}>
          {conversations.length === 0 ? (
            <div
              style={{
                padding: "32px 16px",
                color: t.textMuted,
                fontSize: 12,
                textAlign: "center",
                lineHeight: 2,
              }}
            >
              Henüz sohbet yok.
              <br />+ ile başlat.
            </div>
          ) : (
            conversations.map((c) => (
              <div
                key={c.conversation_id}
                onClick={() => {
                  if (
                    String(selectedConv?.conversation_id) ===
                    String(c.conversation_id)
                  ) {
                    setSelectedConv(null);
                  } else {
                    setSelectedConv(c);
                    setMessages([]);
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "9px 10px",
                  cursor: "pointer",
                  borderRadius: 9,
                  marginBottom: 2,
                  background:
                    String(selectedConv?.conversation_id) ===
                    String(c.conversation_id)
                      ? `${t.accent}1a`
                      : "transparent",
                  transition: "background 0.15s",
                }}
              >
                <AvatarEl user={c.user} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{ fontSize: 12, fontWeight: 500, color: t.text }}
                    >
                      {fullName(c.user)}
                    </div>
                    {c.unread_count > 0 && (
                      <div
                        style={{
                          background: t.accent,
                          color: t.accentText,
                          fontSize: 10,
                          fontWeight: 600,
                          borderRadius: 10,
                          padding: "1px 6px",
                          minWidth: 18,
                          textAlign: "center",
                        }}
                      >
                        {c.unread_count}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: c.unread_count > 0 ? t.text : t.textMuted,
                      fontWeight: c.unread_count > 0 ? 500 : 400,
                      marginTop: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c.last_message?.content
                      ? (c.last_message.from_me ? "Sen: " : "") +
                        c.last_message.content.slice(0, 26)
                      : "Henüz mesaj yok"}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation(c.conversation_id);
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: t.textMuted,
                    cursor: "pointer",
                    padding: 4,
                    borderRadius: 5,
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <X size={11} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* SAĞ PANEL */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {!selectedConv ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
            }}
          >
            <MessageSquare size={40} color={t.textMuted} strokeWidth={1} />
            <div style={{ color: t.textMuted, fontSize: 13 }}>
              Bir sohbet seç veya yeni başlat.
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                padding: "13px 18px",
                borderBottom: `1px solid ${t.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <AvatarEl user={selectedConv.user} size={34} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: t.text }}>
                    {fullName(selectedConv.user)}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      marginTop: 2,
                      color:
                        selectedConv.user?.status === "online"
                          ? "#5a9a5a"
                          : t.textMuted,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        background:
                          selectedConv.user?.status === "online"
                            ? "#5a9a5a"
                            : t.textMuted,
                        display: "inline-block",
                      }}
                    />
                    {selectedConv.user?.status === "online"
                      ? "çevrimiçi"
                      : "çevrimdışı"}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={handleExport}
                  style={{
                    background: "transparent",
                    border: `1px solid ${t.border}`,
                    borderRadius: 8,
                    color: t.textSecondary,
                    cursor: "pointer",
                    padding: "6px 13px",
                    fontSize: 11,
                    fontFamily: "inherit",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    letterSpacing: 0.2,
                  }}
                >
                  <Download size={12} /> Dışa Aktar
                </button>
                <button
                  onClick={() => setSelectedConv(null)}
                  style={{
                    background: "transparent",
                    border: `1px solid ${t.border}`,
                    borderRadius: 8,
                    color: t.textMuted,
                    cursor: "pointer",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <X size={13} />
                </button>
              </div>
            </div>

            {interrupt && (
              <div
                style={{
                  background: t.interrupt,
                  borderBottom: `1px solid ${t.interruptBorder}`,
                  color: t.interruptText,
                  padding: "7px 18px",
                  fontSize: 11,
                  flexShrink: 0,
                  letterSpacing: 0.2,
                }}
              >
                {interrupt.name} konuşmayı kesti
              </div>
            )}

            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "18px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {messagesLoading ? (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: t.textMuted,
                    fontSize: 12,
                  }}
                >
                  Yükleniyor...
                </div>
              ) : (
                <>
                  {messages.map((msg, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: msg.from_me ? "flex-end" : "flex-start",
                      }}
                    >
                      <div
                        style={{
                          maxWidth: 340,
                          borderRadius: msg.from_me
                            ? "13px 13px 3px 13px"
                            : "13px 13px 13px 3px",
                          padding: "9px 13px",
                          wordBreak: "break-word",
                          background: msg.from_me ? t.accent : t.bubblePeer,
                          border: msg.from_me
                            ? "none"
                            : `1px solid ${t.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 13,
                            lineHeight: 1.55,
                            color: msg.from_me ? t.accentText : t.text,
                          }}
                        >
                          {msg.content}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            opacity: 0.55,
                            marginTop: 4,
                            textAlign: "right",
                            color: msg.from_me ? t.accentText : t.textMuted,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 3,
                          }}
                        >
                          {formatTime(msg.timestamp)}
                          <ReadIcon msg={msg} />
                        </div>
                      </div>
                    </div>
                  ))}

                  {peerInterim !== null && (
                    <div
                      style={{ display: "flex", justifyContent: "flex-start" }}
                    >
                      <div
                        style={{
                          maxWidth: 340,
                          borderRadius: "13px 13px 13px 3px",
                          padding: "9px 13px",
                          background: t.bubblePeer,
                          border: `1px solid ${t.border}`,
                          opacity: 0.85,
                        }}
                      >
                        {peerInterim.text ? (
                          <div
                            style={{
                              fontSize: 13,
                              lineHeight: 1.55,
                              color: t.text,
                              fontStyle: "italic",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            {peerInterim.text}
                            <span
                              style={{
                                display: "inline-block",
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: "#7eb8f7",
                                flexShrink: 0,
                                animation: "blink 1s infinite",
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <Mic
                              size={13}
                              color="#c0392b"
                              style={{
                                animation: "micPulse 1.5s infinite",
                                flexShrink: 0,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {peerTyping && peerInterim === null && (
                    <div
                      style={{ display: "flex", justifyContent: "flex-start" }}
                    >
                      <div
                        style={{
                          background: t.bubblePeer,
                          border: `1px solid ${t.border}`,
                          borderRadius: "13px 13px 13px 3px",
                          padding: "11px 15px",
                          display: "flex",
                          gap: 4,
                          alignItems: "center",
                        }}
                      >
                        {[0, 0.2, 0.4].map((d, i) => (
                          <span
                            key={i}
                            style={{
                              display: "inline-block",
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              background: t.textMuted,
                              animation: `blink 1.2s infinite ${d}s`,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            <form
              onSubmit={handleSend}
              style={{
                display: "flex",
                gap: 8,
                padding: "12px 16px",
                borderTop: `1px solid ${t.border}`,
                flexShrink: 0,
                alignItems: "flex-end",
              }}
            >
              <textarea
                style={{
                  flex: 1,
                  background: t.input,
                  border: `1px solid ${isDictating ? t.accent : t.inputBorder}`,
                  borderRadius: 10,
                  padding: "9px 13px",
                  color: t.text,
                  fontSize: 13,
                  fontFamily: "'Inter',sans-serif",
                  fontStyle: isDictating ? "italic" : "normal",
                  resize: "none",
                  lineHeight: 1.5,
                  maxHeight: 110,
                  overflowY: "auto",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                value={newMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={emitTypingStop}
                placeholder={isDictating ? "Dinleniyor..." : "Mesajını yaz..."}
                rows={1}
                readOnly={isDictating}
              />

              <button
                type="button"
                onClick={handleMicToggle}
                title={
                  isDictating
                    ? newMessage.trim()
                      ? "Gönder"
                      : "İptal"
                    : "Dikte başlat"
                }
                style={{
                  background: isDictating ? "#c0392b" : "transparent",
                  border: `1px solid ${isDictating ? "#c0392b" : t.border}`,
                  borderRadius: 10,
                  color: isDictating ? "#fff" : t.textMuted,
                  cursor: "pointer",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "all 0.2s",
                  animation: isDictating ? "micPulse 1.5s infinite" : "none",
                }}
              >
                {isDictating ? <MicOff size={15} /> : <Mic size={15} />}
              </button>

              <button
                type="submit"
                onClick={handleSend}
                disabled={!newMessage.trim()}
                style={{
                  background: t.accent,
                  border: "none",
                  borderRadius: 10,
                  color: t.accentText,
                  cursor: "pointer",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  opacity: !newMessage.trim() ? 0.45 : 1,
                  transition: "opacity 0.2s",
                }}
              >
                <Send size={15} />
              </button>
            </form>
          </>
        )}
      </div>

      <style>{`
        @keyframes blink { 0%,100%{opacity:0.2;transform:scale(0.85)} 50%{opacity:1;transform:scale(1.15)} }
        @keyframes micPulse { 0%,100%{box-shadow:0 0 0 0 rgba(192,57,43,0.4)} 50%{box-shadow:0 0 0 6px rgba(192,57,43,0)} }
        textarea:focus { outline:none !important; }
        ::-webkit-scrollbar { width:3px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:${t.scrollThumb}; border-radius:3px; }
      `}</style>
    </div>
  );
}
